import { useState, useEffect } from "react"
import { X, RefreshCw, CheckCircle, AlertCircle } from "lucide-react"
import { showToast } from "../utils/toastUtils"

const UpdateOrderModal = ({ isOpen, onClose, order, onUpdate }) => {
  const [status, setStatus] = useState("New")
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (order) {
      setStatus(order.status || "New")
    }
  }, [order])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const success = await onUpdate(order._id, { status })
      if (success) {
        onClose()
      }
    } catch (error) {
      console.error("Error in form submission:", error)
      showToast("Error", "Failed to update order", "error")
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen || !order) return null

  const ALLOWED_STATUSES = [
    "New",
    "Pending Confirm",
    "Pending Packed",
    "Packed",
    "Cancelled"
  ];

  // Include the current status to prevent accidental updates when opening the modal
  const STATUSES = Array.from(new Set([order.status, ...ALLOWED_STATUSES]));

  const wasPacked = ["Packed", "Pending Packed"].includes(order.status);
  const goingToNewOrPending = status === "New" || status === "Pending Confirm";
  const showCourierWarning = wasPacked && goingToNewOrPending;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-blue-600" />
              Update Order Status
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Change status for Order #{order.orderId}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Current Status: {order.status}</p>
                <p className="opacity-80">Make sure to inform the customer if this change affects their delivery timeline.</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Status
              </label>
              <div className="relative">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full rounded-xl border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none transition-all appearance-none cursor-pointer hover:bg-white hover:border-blue-300"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>

            {showCourierWarning && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3 mt-4 transition-all">
                <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div className="text-sm text-red-800">
                  <p className="font-bold mb-1">Warning: Tracking Number will be cleared!</p>
                  <p className="opacity-90">
                    You have to delete the responding order from the Courier admin application manually.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="mt-8 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-600/20 transition-all transform active:scale-95 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>Updating...</>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Update Status
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default UpdateOrderModal
